import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setSession } from '../admin/sessionSlice';
import { setBackButtonUrl, setCompanyLogoUrl } from './uiSlice';
import { ESCAPE_TO_INNER_PEACE_LOCAL_TEST_GAME_ID } from './login/escapeLocalGuestRouting';

const USER_DATA_KEY = 'userData';
const SESSION_KEY = 'session';

const uuid4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const isTrueQueryParam = (value) =>
  typeof value === 'string' && value.trim().toLowerCase() === 'true';

const readRawStoredUserData = () => {
  let stored = sessionStorage.getItem(USER_DATA_KEY);
  if (!stored) {
    const legacy = localStorage.getItem(USER_DATA_KEY);
    if (legacy) {
      sessionStorage.setItem(USER_DATA_KEY, legacy);
      localStorage.removeItem(USER_DATA_KEY);
      stored = legacy;
    }
  }
  return stored;
};

const normalizeStoredUserData = (stored) => {
  if (!stored || typeof stored !== 'object') return null;

  const userId = String(stored.userId || stored.userid || stored.id || '').trim();
  const sessionId = String(stored.sessionId || '').trim();
  const organizationId = String(stored.organizationId || '').trim();
  const gameId = String(stored.gameId || '').trim();
  const role = String(stored.role || stored.source || '').trim();
  const source = String(stored.source || stored.role || '').trim();
  const sessionMode = String(stored.sessionMode || '').trim();

  if (!userId || !sessionId || !organizationId || !gameId || !role) {
    return null;
  }

  if (String(localStorage.getItem(SESSION_KEY) || '').trim() !== `${sessionId}&${organizationId}`) {
    return null;
  }

  return {
    ...stored,
    userId,
    sessionId,
    organizationId,
    gameId,
    role,
    source,
    sessionMode: sessionMode || undefined,
  };
};

async function fetchStageReportMeta({ sessionId, organizationId, userId }) {
  const empty = { gameover: 0, reportId: null };
  try {
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (!backendBase || !sessionId || !organizationId || !userId) return empty;

    const res = await fetch(`${backendBase}/fetchReport`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionId}&${organizationId}`,
      },
      body: JSON.stringify({ userId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.success || !data.report) return empty;

    const gameoverRaw = data.report.gameover;
    const gameover = gameoverRaw == null || gameoverRaw === '' ? 0 : Number(gameoverRaw);
    const reportIdRaw = data.report.reportId;
    const reportId =
      reportIdRaw != null && String(reportIdRaw).trim() !== ''
        ? String(reportIdRaw).trim()
        : null;

    return {
      gameover: Number.isFinite(gameover) ? gameover : 0,
      reportId,
    };
  } catch {
    return empty;
  }
}

async function restoreStoredUserData({ dispatch, fromMobileApp }) {
  const rawStored = readRawStoredUserData();
  if (!rawStored) return null;

  let parsed;
  try {
    parsed = JSON.parse(rawStored);
  } catch {
    return null;
  }

  let userData = normalizeStoredUserData(parsed);
  if (!userData) return null;

  const persistedFromMobileApp = isTrueQueryParam(String(userData?.fromMobileApp ?? ''));
  const finalFromMobileApp = fromMobileApp || persistedFromMobileApp;
  if (finalFromMobileApp !== persistedFromMobileApp) {
    userData = { ...userData, fromMobileApp: finalFromMobileApp };
  }

  dispatch(
    setSession({
      sessionId: userData.sessionId,
      organizationId: userData.organizationId,
    })
  );
  dispatch(setCompanyLogoUrl(userData.companyLogoUrl || process.env.REACT_APP_EM_LOGO || null));
  dispatch(setBackButtonUrl(userData.backButtonRedirect || null));

  const stageMeta = await fetchStageReportMeta({
    sessionId: userData.sessionId,
    organizationId: userData.organizationId,
    userId: userData.userId,
  });

  const reportId = stageMeta.reportId || userData.reportId || userData.report_id || null;
  const { report_id: _drop, ...rest } = userData;
  userData = { ...rest, gameover: stageMeta.gameover };
  if (reportId) userData.reportId = String(reportId).trim();

  sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  return userData;
}

async function finalizeLogin({ userResponseData, demo, fromMobileApp }, dispatch) {
  dispatch(
    setSession({
      sessionId: userResponseData.sessionId,
      organizationId: userResponseData.organizationId,
    })
  );

  const userData = {
    userId: userResponseData.userId || userResponseData.id,
    token: userResponseData.token || '',
    name: `${userResponseData.firstName} ${userResponseData.lastName}`.trim(),
    email: userResponseData.email || userResponseData.employeeId,
    employeeId:
      userResponseData.employeeId || userResponseData.userCustomFieldsData?.employeeId || null,
    sessionId: userResponseData.sessionId,
    organizationId: userResponseData.organizationId,
    gameId: userResponseData.gameId,
    source: demo ? 'DEMO' : userResponseData.role,
    expiry: Date.now() + 24 * 60 * 60 * 1000,
    role: userResponseData.role,
    sessionMode: userResponseData.sessionMode || undefined,
    companyLogoUrl: process.env.REACT_APP_EM_LOGO,
    backButtonRedirect: process.env.REACT_APP_BASE_URL,
    gameover: 0,
    fromMobileApp,
  };

  if (!demo) {
    const stageMeta = await fetchStageReportMeta({
      sessionId: userData.sessionId,
      organizationId: userData.organizationId,
      userId: userData.userId,
    });
    userData.gameover = stageMeta.gameover;
    if (stageMeta.reportId) {
      userData.reportId = stageMeta.reportId;
    }
  }

  if (!demo) {
    const orgRes = await axios.get(
      `${process.env.REACT_APP_ORG_API}/${userResponseData.organizationId}`
    );
    if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
      if (!orgRes.data?.organization?.companyLogo) {
        userData.companyLogoUrl = process.env.REACT_APP_EM_LOGO;
      } else {
        const s3Source = process.env.REACT_APP_S3_SOURCE || '';
        const base = s3Source.endsWith('/') ? s3Source : `${s3Source}/`;
        userData.companyLogoUrl = `${base}${orgRes.data.organization.companyLogo}`;
      }
      userData.backButtonRedirect = `${process.env.REACT_APP_BASE_URL}/game-detail/${userResponseData.gameId}`;
    }
  }

  dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
  dispatch(setBackButtonUrl(userData.backButtonRedirect || null));

  try {
    localStorage.removeItem(USER_DATA_KEY);
  } catch (_) {
    /* ignore */
  }
  sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));

  return userData;
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');
      const localGame = queryParams.get('game');
      const fromMobileApp = isTrueQueryParam(queryParams.get('fromMobileApp'));
      const otp = queryParams.get('otp');

      if (save) {
        const userData = await restoreStoredUserData({ dispatch, fromMobileApp });
        if (!userData) return rejectWithValue('No saved user data found.');
        return userData;
      }

      let userResponseData;
      if (demo) {
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000)
                  .toString()
                  .padStart(3, '0')}`,
                sessionId: 'demo' + Math.floor(Math.random() * 100000),
                organizationId: 'demo' + Math.floor(Math.random() * 100000),
                gameId: localGame === 'escape' ? ESCAPE_TO_INNER_PEACE_LOCAL_TEST_GAME_ID : null,
                role: 'demobypass',
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: process.env.REACT_APP_BASE_URL,
                gameover: 0,
              },
            },
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const role = queryParams.get('role');
        if (!otp) {
          const restoredUser = await restoreStoredUserData({ dispatch, fromMobileApp });
          if (restoredUser) return restoredUser;
          return rejectWithValue('OTP is required for login.');
        }

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        let source = 'ORG_USER';
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          source = 'GUEST_USER';
        }

        const clientSecret =
          process.env.REACT_APP_FIREBASE_CLIENT_SECRET || process.env.REACT_APP_CLIENT_SECRET;
        const headers = {
          'client-secret': clientSecret,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (response.data.message !== 'LOGIN SUCCESS') {
          return rejectWithValue('Login failed');
        }
        response.data.data.role = source;
        userResponseData = response.data.data;
      }

      return await finalizeLogin({ userResponseData, demo, fromMobileApp }, dispatch);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem(USER_DATA_KEY);
        try {
          localStorage.removeItem(USER_DATA_KEY);
        } catch (_) {
          /* ignore */
        }
      }

      const data = error.response?.data;
      const apiMsg =
        (typeof data?.message === 'string' && data.message) || data?.err?.message;
      if (process.env.NODE_ENV !== 'production') {
        console.error('loginUser failed:', error.message);
      }
      return rejectWithValue(apiMsg || 'Unable to sign in. Please try again.');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    status: 'idle',
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.status = 'idle';
      state.error = null;
      sessionStorage.removeItem(USER_DATA_KEY);
      try {
        localStorage.removeItem(USER_DATA_KEY);
      } catch (_) {
        /* ignore */
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.user = null;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
